import type {
  PatientHistoryJobDerivedProvider,
  PatientHistoryJobDerivedStatus,
} from '@ctw/shared/api/fhir/models/patient-history';
import { usePatientHistoryDetails } from '@ctw/shared/api/patient-history/patient-history';
import { usePatientSubscription } from '@ctw/shared/api/subscriptions/subscriptions';
import { Alert } from '@ctw/shared/components/alert';
import { Link } from '@ctw/shared/components/link';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { ZusLogoIconOnly } from '@ctw/shared/components/zus-logo-icon-only';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { RequestRecordsButton } from '@ctw/shared/content/patient-history/request-records-button';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { faCheck, faClock, faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverviewCard } from '../../components/containers/overview-card';
import { usePatientHistory } from '../patient-history/use-patient-history';
import { SupportFormLink } from '../support-form-link';

export const ZusInformationCard = withOverviewCardErrorBoundary({
  cardTitle: 'Records Provided by Zus Health',
  boundaryName: 'ZusInformationCardComponent',
  Component: () => {
    const patientHistory = usePatientHistory();

    return (
      <OverviewCard
        headerIcon={<ZusLogoIconOnly width={25} height={25} />}
        title="Records Provided by Zus Health"
        loading={patientHistory.isLoading}
        testId="zus-information-card"
        empty={patientHistory.isError}
        emptyState="Unable to load records request information"
      >
        <div className={tw`flex h-full flex-col space-y-2`}>
          <div className={tw`flex-1`}>
            <PatientHistoryJobStatusWithAction />
          </div>
          <div className={tw`mt-auto space-y-1.5 font-medium`}>
            <SupportFormLink
              buttonText="Have feedback or need support?"
              className={tw`block text-sm`}
            />
            <Link
              href="https://clinicalguide.zushealth.com/docs/getting-started"
              className={tw`block text-sm`}
              target="_blank"
            >
              Learn more about the ZAP
            </Link>
          </div>
        </div>
      </OverviewCard>
    );
  },
});

const PatientHistoryJobStatusWithAction = () => {
  const patientHistoryDetails = usePatientHistoryDetails();
  const builderConfig = useBuilderConfig();

  if (patientHistoryDetails.isLoading) {
    return <LoadingSpinner message="Loading..." className={tw`w-full`} />;
  }

  if (patientHistoryDetails.isError) {
    return <Alert type="error" header="Error loading data" />;
  }

  const isInProgress = !!patientHistoryDetails.data?.inProgress;
  const job = isInProgress
    ? patientHistoryDetails.data?.inProgress
    : patientHistoryDetails.data?.completed;

  return (
    <div className={tw`@container grid grid-cols-1 gap-y-2`}>
      {/* Top section: Package info and Request Records button */}
      <div className={tw`flex items-start`}>
        <PatientSubscriptionPackage />
        <div className={tw`ml-auto`}>
          {builderConfig.showRequestRecords && <RequestRecordsButton />}
        </div>
      </div>

      {job ? (
        <>
          <div>
            <div className={tw`text-xs`}>
              {isInProgress ? 'Request in progress...' : `Last synced ${job?.lastUpdatedAt}`}
            </div>
          </div>
          {job?.ehrNetworkStatus && (
            <DataProviderStatus
              status={job?.ehrNetworkStatus}
              name="EHR Networks"
              subProviders={job?.derivedProviders.filter((provider) => provider.isEhrNetwork)}
            />
          )}

          {job?.derivedProviders
            .filter((provider) => !provider.isEhrNetwork)
            .map((provider) => (
              <DataProviderStatus
                key={provider.service}
                status={provider.derivedStatus}
                name={provider.serviceDisplayName}
              />
            ))}
        </>
      ) : (
        <div>No patient history requests have been run for this patient.</div>
      )}
    </div>
  );
};

const DataProviderStatus = ({
  status,
  name,
  subProviders: providers,
}: {
  status: PatientHistoryJobDerivedStatus;
  name: string;
  subProviders?: Array<PatientHistoryJobDerivedProvider>;
}) => {
  return (
    <div className={tw`rounded border border-background-subtle bg-background px-2 py-2`}>
      <div className={tw`flex items-center`}>
        <DataProviderStatusIcon status={status} />
        <div className={tw`font-medium`}>{name}</div>
      </div>

      {providers &&
        providers.length > 0 &&
        // only show sub-provider details if the statuses are different
        !providers.every((provider) => provider.derivedStatus === providers[0].derivedStatus) && (
          <div className={tw`mt-1 ml-6 space-y-1`}>
            {providers.map((provider) => (
              <div key={provider.service} className={tw`flex items-center`}>
                <DataProviderStatusIcon status={provider.derivedStatus} />
                <div className={tw`text-xs `}>{provider.serviceDisplayName}</div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

const DataProviderStatusIcon = ({ status }: { status: PatientHistoryJobDerivedStatus }) => {
  let statusIcon: IconDefinition;
  let statusLabel: string;

  switch (status) {
    case 'complete':
      statusIcon = faCheck;
      statusLabel = 'Done';
      break;
    case 'inProgress':
    case 'partiallyComplete':
      statusIcon = faClock;
      statusLabel = 'In Progress';
      break;
    case 'failed':
    case 'completeWithErrors':
      statusIcon = faExclamationTriangle;
      statusLabel = 'Error';
      break;
    default:
      statusIcon = faExclamationTriangle;
      statusLabel = status;
  }

  return (
    <Tooltip
      id={`provider-status-tooltip-${status}-${Math.random().toString(10)}`}
      content={statusLabel}
    >
      <span>
        <FontAwesomeIcon icon={statusIcon} className={tw`mr-2`} />
      </span>
    </Tooltip>
  );
};

const PatientSubscriptionPackage = () => {
  const patientSubscription = usePatientSubscription();

  if (patientSubscription.isLoading) {
    return <LoadingSpinner message="Loading..." />;
  }

  if (patientSubscription.isError) {
    return <Alert type="error" header="Error loading data" />;
  }

  const patientPackage = patientSubscription.data?.package;

  if (!patientPackage) {
    return null;
  }

  return <div className={tw`font-semibold`}>Enrolled in {patientPackage.name}</div>;
};
